import { SessionStatus, useSessionRestore } from '../../hooks/useSessionRestore';
import { BrandedLoadingOverlay } from '../loader/branded-loader';
import type { ReactNode } from 'react';

interface SessionBootstrapProps {
  children: ReactNode;
}

/**
 * Top-level gate that settles the auth session before rendering the app tree.
 *
 * While a load-time refresh is in flight (`Restoring`) it shows the branded
 * full-page loader and withholds `children`, so providers below it — notably
 * `UserProvider`, whose user fetch is gated on a valid access token — mount
 * only once the token is valid. This prevents the "logged out on a new tab"
 * flash where an expired access token would otherwise redirect to login before
 * the still-valid refresh token is used.
 *
 * @param {SessionBootstrapProps} props - Component props
 * @returns {ReactNode} The loader while restoring, otherwise the children
 */
export function SessionBootstrap({ children }: SessionBootstrapProps): ReactNode {
  const status = useSessionRestore();

  if (status === SessionStatus.Restoring) {
    // role="status" gives assistive tech a live-region cue while the entire app
    // is withheld; without it a screen-reader user gets no feedback during the
    // load-time restore.
    return (
      <div role="status" aria-label="Restoring your session">
        <BrandedLoadingOverlay visible variant="colored" />
      </div>
    );
  }

  return children;
}
